import { useCallback, useEffect, useState, type DragEvent } from 'react';
import { addGalleryImages } from '../db/gallery';
import { couldntReadLabel } from '../ui/counts';

/**
 * Photos arriving at the open item: dropped on the panel, pasted, or picked
 * from the one file input the panel owns. The board's equivalent makes a new
 * item per file; this one adds them all to the item you're looking at.
 */
export function usePhotoIntake({
  itemId,
  enabled,
}: {
  itemId: string;
  /** False while a dialog or the lightbox owns the keyboard. */
  enabled: boolean;
}) {
  const [dropping, setDropping] = useState(false);
  const [adding, setAdding] = useState(0);
  const [error, setError] = useState<string | undefined>(undefined);

  const add = useCallback(
    async (files: readonly File[]) => {
      const images = files.filter((file) => file.type.startsWith('image/'));
      if (images.length === 0) return;

      setAdding(images.length);
      setError(undefined);
      try {
        const result = await addGalleryImages(itemId, images);
        if (result.failed.length > 0) setError(couldntReadLabel(result.failed));
      } catch {
        setError("Those photos couldn't be added. Nothing was saved.");
      } finally {
        setAdding(0);
      }
    },
    [itemId],
  );

  // A pasted screenshot belongs to the open item, not to a new one — the
  // board's own paste handler stands down while the panel is up.
  useEffect(() => {
    if (!enabled) return;

    function onPaste(event: ClipboardEvent) {
      const target = event.target;
      if (target instanceof HTMLElement && target.closest('input, textarea, [contenteditable]')) {
        return;
      }

      const files = [...(event.clipboardData?.files ?? [])];
      if (files.length === 0) return;
      event.preventDefault();
      void add(files);
    }

    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [enabled, add]);

  const handlers = {
    onDragEnter: (event: DragEvent) => {
      if (carriesFiles(event)) setDropping(true);
    },
    onDragOver: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      // Without this the browser navigates to the dropped file.
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: (event: DragEvent) => {
      // Only the pointer actually leaving the panel, not crossing a child.
      const to = event.relatedTarget;
      if (!(to instanceof Node) || !event.currentTarget.contains(to)) setDropping(false);
    },
    onDrop: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      setDropping(false);
      void add([...event.dataTransfer.files]);
    },
  };

  return {
    dropping,
    adding,
    error,
    dismissError: () => setError(undefined),
    add: (files: readonly File[]) => void add(files),
    handlers,
  };
}

/** A gallery reorder carries an image id, not files. Only files land here. */
function carriesFiles(event: DragEvent): boolean {
  return event.dataTransfer.types.includes('Files');
}
